import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Divider, useMediaQuery,
  Collapse, Tooltip, Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import AddHomeWorkRoundedIcon from '@mui/icons-material/AddHomeWorkRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import { projectname } from '../utils/Apis';
import { Constants } from '../utils/Constants';
import { SIDEBAR_WIDTH } from '../theme/theme';

/**
 * Modern app shell (dark sidebar + translucent topbar) for the Admin panel.
 * Drop-in replacement for the legacy Midone SideMenu + TopBar + MobileMenu.
 * Navigation targets and active-route logic mirror the originals exactly, so
 * routing behaviour is unchanged.
 */
const AppShell = ({ title = 'Dashboard', breadcrumb = [], children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const isSA = (localStorage.getItem(Constants.USERTYPE) || '') === 'SA';
  const isHost = (localStorage.getItem(Constants.USERTYPE) || '') === 'SubAdmin';
  const userGroupActive = ['/sub_admin_list', '/create_sub_admin', '/'].includes(pathname);
  const roomGroupActive = ['/room_list', '/create_room'].includes(pathname);
  const [openUsers, setOpenUsers] = React.useState(userGroupActive);
  const [openRooms, setOpenRooms] = React.useState(roomGroupActive);

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const flatItems = [
    { label: 'Profile', icon: <AccountCircleRoundedIcon />, path: '/profile' },
    { label: 'Cloud Recordings', icon: <CloudDownloadRoundedIcon />, path: '/cloud_recordings' },
    { label: 'Messages', icon: <ForumRoundedIcon />, path: '/messages' },
    { label: 'Online Users', icon: <WifiTetheringRoundedIcon />, path: '/online-users' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2, display: 'grid', placeItems: 'center',
          background: 'linear-gradient(135deg,#38bdf8,#2563eb 55%,#312e81)', color: '#fff', fontWeight: 800,
        }}>Z</Box>
        <Typography variant="h6" sx={{ color: '#fff', letterSpacing: '0.02em' }}>
          {projectname}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {isSA ? (
        <List sx={{ px: 1.5, py: 1.5, flex: 1, overflowY: 'auto' }}>
          <Typography variant="caption" sx={{ px: 1.5, color: '#64748b', fontWeight: 700, letterSpacing: '0.08em' }}>
            ADMIN MANAGEMENT
          </Typography>
          <ListItemButton selected={['/admin_list', '/'].includes(pathname)} onClick={() => go('/admin_list')}>
            <ListItemIcon><SupervisorAccountRoundedIcon /></ListItemIcon>
            <ListItemText primary="Admins" />
          </ListItemButton>
          <ListItemButton selected={pathname === '/create_admin'} onClick={() => go('/create_admin')}>
            <ListItemIcon><PersonAddAlt1RoundedIcon /></ListItemIcon>
            <ListItemText primary="Add Admin" />
          </ListItemButton>
        </List>
      ) : isHost ? (
        // Host (SubAdmin): their own room's participants + recordings, nothing else.
        <List sx={{ px: 1.5, py: 1.5, flex: 1, overflowY: 'auto' }}>
          <Typography variant="caption" sx={{ px: 1.5, color: '#64748b', fontWeight: 700, letterSpacing: '0.08em' }}>
            MY ROOM
          </Typography>
          <ListItemButton selected={['/sub_admin_list', '/'].includes(pathname)} onClick={() => go('/sub_admin_list')}>
            <ListItemIcon><GroupsRoundedIcon /></ListItemIcon>
            <ListItemText primary="Participants" />
          </ListItemButton>
          <ListItemButton selected={pathname === '/create_sub_admin'} onClick={() => go('/create_sub_admin')}>
            <ListItemIcon><PersonAddAlt1RoundedIcon /></ListItemIcon>
            <ListItemText primary="Add Participant" />
          </ListItemButton>
          <ListItemButton selected={pathname === '/cloud_recordings'} onClick={() => go('/cloud_recordings')}>
            <ListItemIcon><CloudDownloadRoundedIcon /></ListItemIcon>
            <ListItemText primary="Recordings" />
          </ListItemButton>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />
          <ListItemButton selected={pathname === '/profile'} onClick={() => go('/profile')}>
            <ListItemIcon><AccountCircleRoundedIcon /></ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>
        </List>
      ) : (
      <List sx={{ px: 1.5, py: 1.5, flex: 1, overflowY: 'auto' }}>
        <Typography variant="caption" sx={{ px: 1.5, color: '#64748b', fontWeight: 700, letterSpacing: '0.08em' }}>
          USER MANAGEMENT
        </Typography>
        <ListItemButton onClick={() => setOpenUsers((v) => !v)} selected={userGroupActive && pathname !== '/sub_admin_list' && pathname !== '/create_sub_admin'}>
          <ListItemIcon><GroupsRoundedIcon /></ListItemIcon>
          <ListItemText primary="Users" />
          {openUsers ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openUsers} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 2 }}>
            <ListItemButton selected={pathname === '/sub_admin_list' || pathname === '/'} onClick={() => go('/sub_admin_list')}>
              <ListItemIcon><ListAltRoundedIcon /></ListItemIcon>
              <ListItemText primary="User List" />
            </ListItemButton>
            <ListItemButton selected={pathname === '/create_sub_admin'} onClick={() => go('/create_sub_admin')}>
              <ListItemIcon><PersonAddAlt1RoundedIcon /></ListItemIcon>
              <ListItemText primary="Add User" />
            </ListItemButton>
          </List>
        </Collapse>

        <Typography variant="caption" sx={{ px: 1.5, mt: 1.5, display: 'block', color: '#64748b', fontWeight: 700, letterSpacing: '0.08em' }}>
          ROOM MANAGEMENT
        </Typography>
        <ListItemButton onClick={() => setOpenRooms((v) => !v)} selected={roomGroupActive}>
          <ListItemIcon><MeetingRoomRoundedIcon /></ListItemIcon>
          <ListItemText primary="Rooms" />
          {openRooms ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openRooms} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 2 }}>
            <ListItemButton selected={pathname === '/room_list'} onClick={() => go('/room_list')}>
              <ListItemIcon><ListAltRoundedIcon /></ListItemIcon>
              <ListItemText primary="Room List" />
            </ListItemButton>
            <ListItemButton selected={pathname === '/create_room'} onClick={() => go('/create_room')}>
              <ListItemIcon><AddHomeWorkRoundedIcon /></ListItemIcon>
              <ListItemText primary="Add Room" />
            </ListItemButton>
          </List>
        </Collapse>

        <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />
        {flatItems.map((item) => (
          <ListItemButton key={item.path} selected={pathname === item.path} onClick={() => go(item.path)}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          onClick={() => { localStorage.clear(); navigate('/login'); }}
          sx={{ color: '#fca5a5', '&:hover': { backgroundColor: 'rgba(239,68,68,0.12)', color: '#fecaca' } }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><LogoutRoundedIcon /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', position: 'relative' }}>
      {/* zaudio-style animated glass backdrop */}
      <div className="aurora" />
      <div className="grid-veil" />

      <AppBar position="fixed" sx={{ width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` }, ml: { md: `${SIDEBAR_WIDTH}px` }, zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>{title}</Typography>
            <Breadcrumbs sx={{ fontSize: 12 }} aria-label="breadcrumb">
              <MuiLink underline="hover" color="text.secondary" href="#" onClick={(e) => e.preventDefault()}>Home</MuiLink>
              {breadcrumb.map((b) => (
                <Typography key={b} color="text.secondary" sx={{ fontSize: 12 }}>{b}</Typography>
              ))}
            </Breadcrumbs>
          </Box>
          <Tooltip title="Account">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 15 }}>
              {(projectname || 'A').charAt(0)}
            </Avatar>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: SIDEBAR_WIDTH } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` }, p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default AppShell;
